import { useState, useRef, useEffect, useCallback } from 'react'
import { useTheme } from '../hooks/useTheme'
import { aiApi } from '../services/api'

export default function AIPage() {
  const { bgPrimary, bgWhite, textPrimary, textMuted, isDark } = useTheme()

  const [messages, setMessages] = useState([
    {
      id: 1,
      role: 'assistant',
      content: '你好！我是AI学习助手 🤖\n\n有什么可以帮助你的？你可以问我：\n📚 课程相关问题\n📖 知识点讲解\n💡 学习方法建议\n❓ 作业难题解答',
      time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
    }
  ])
  const [inputText, setInputText] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [favorites, setFavorites] = useState([])

  // 长按菜单状态
  const [menuVisible, setMenuVisible] = useState(false)
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 })
  const [selectedMsg, setSelectedMsg] = useState(null)

  // 长按检测
  const longPressTimer = useRef(null)
  const longPressDelay = 500 // 500ms 触发长按

  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // 清除长按定时器
  const clearLongPress = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }, [])

  // 开始长按检测
  const startLongPress = useCallback((e, msg) => {
    clearLongPress()
    setSelectedMsg(msg)
    longPressTimer.current = setTimeout(() => {
      // 获取触摸/鼠标位置
      const clientX = e.touches ? e.touches[0].clientX : e.clientX
      const clientY = e.touches ? e.touches[0].clientY : e.clientY
      setMenuPosition({ x: clientX, y: clientY })
      setMenuVisible(true)
    }, longPressDelay)
  }, [clearLongPress])

  // 调用后端 AI API（DeepSeek + 计费）
  const callAI = useCallback(async (userMessage) => {
    const requestId = 'req_' + Date.now() + '_' + Math.random().toString(36).slice(2, 10)
    setIsTyping(true)

    try {
      const res = await aiApi.chat(userMessage, requestId)

      if (res.insufficientBalance) {
        setMessages(prev => [...prev, {
          id: Date.now() + 1,
          role: 'assistant',
          content: '余额不足，请充值后继续使用AI问答。',
          time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }),
          isSystem: true,
        }])
        setIsTyping(false)
        return
      }

      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        role: 'assistant',
        content: res.reply || 'AI 暂时无法回答，请稍后再试。',
        time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }),
      }])
    } catch (err) {
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        role: 'assistant',
        content: '网络异常，请检查网络后重试。',
        time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }),
        isSystem: true,
      }])
    } finally {
      setIsTyping(false)
    }
  }, [])

  const handleSend = () => {
    if (!inputText.trim() || isTyping) return
    const newMsg = {
      id: Date.now(),
      role: 'user',
      content: inputText.trim(),
      time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
    }
    setMessages(prev => [...prev, newMsg])
    setInputText('')
    callAI(inputText.trim())
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleClear = () => {
    setMessages([
      {
        id: 1,
        role: 'assistant',
        content: '对话已清空！我们重新开始吧！😊\n\n有什么可以帮助你的？',
        time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
      }
    ])
  }

  // 删除单条消息
  const handleDeleteMsg = (msgId) => {
    setMessages(prev => prev.filter(m => m.id !== msgId))
    setMenuVisible(false)
  }

  // 复制消息内容
  const handleCopyMsg = (content) => {
    navigator.clipboard.writeText(content).then(() => {
      // 可以加个 toast 提示
    }).catch(() => {
      // fallback
      const textarea = document.createElement('textarea')
      textarea.value = content
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
    })
    setMenuVisible(false)
  }

  // 收藏消息
  const handleFavoriteMsg = (msg) => {
    setFavorites(prev => {
      // 避免重复收藏
      if (prev.find(f => f.id === msg.id)) return prev
      return [...prev, { ...msg, favoriteTime: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }) }]
    })
    setMenuVisible(false)
  }

  // 点击其他地方关闭菜单
  useEffect(() => {
    if (menuVisible) {
      const handleClick = () => setMenuVisible(false)
      setTimeout(() => {
        document.addEventListener('click', handleClick)
      }, 100)
      return () => document.removeEventListener('click', handleClick)
    }
  }, [menuVisible])

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 64px)', background: bgPrimary }}>
      {/* 顶栏 - 适配刘海屏 */}
      <div className="flex-shrink-0 px-4 header-height safe-area-top flex items-center justify-end"
           style={{ background: bgWhite, boxShadow: '0 1px 0 rgba(0,0,0,0.06)' }}>
        <button
          onClick={handleClear}
          className="text-xs border-none bg-transparent cursor-pointer px-2 py-1 rounded-lg transition-colors"
          style={{ color: textMuted, background: 'transparent' }}
          onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          <i className="fa-solid fa-trash-alt mr-1" />
          清空
        </button>
      </div>

      {/* 消息列表 */}
      <div className="flex-1 overflow-y-auto px-3 py-4" style={{ scrollBehavior: 'smooth' }}>
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex mb-4 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            {/* AI头像 */}
            {msg.role === 'assistant' && (
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm flex-shrink-0 mr-2.5 shadow-sm"
                   style={{ background: 'linear-gradient(135deg, #6C63FF, #a29bfe)' }}>
                <i className="fa-solid fa-robot" />
              </div>
            )}

            {/* 消息气泡 */}
            <div
              className={`max-w-[78%] px-4 py-3 ${
                msg.role === 'user'
                  ? 'text-white rounded-2xl rounded-br-md'
                  : 'rounded-2xl rounded-bl-md'
              }`}
              style={
                msg.role === 'user'
                  ? { background: 'linear-gradient(135deg, #6C63FF, #8B5CF6)', color: '#fff', boxShadow: '0 2px 12px rgba(108,99,255,0.25)' }
                  : { background: bgWhite, color: textPrimary, boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.06)' }
              }
              onTouchStart={e => startLongPress(e, msg)}
              onTouchEnd={clearLongPress}
              onTouchMove={clearLongPress}
              onMouseDown={e => startLongPress(e, msg)}
              onMouseUp={clearLongPress}
              onMouseLeave={clearLongPress}
            >
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
              <p className="text-[10px] mt-1.5" style={{ color: msg.role === 'user' ? 'rgba(255,255,255,0.6)' : textMuted }}>
                {msg.time}
              </p>
            </div>

            {/* 用户头像 */}
            {msg.role === 'user' && (
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm flex-shrink-0 ml-2.5 shadow-sm"
                   style={{ background: 'linear-gradient(135deg, #FF6B6B, #ee5a24)' }}>
                <i className="fa-solid fa-user" />
              </div>
            )}
          </div>
        ))}

        {/* 正在输入指示器 */}
        {isTyping && (
          <div className="flex mb-4 justify-start">
            <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm mr-2.5 shadow-sm"
                 style={{ background: 'linear-gradient(135deg, #6C63FF, #a29bfe)' }}>
              <i className="fa-solid fa-robot" />
            </div>
            <div className="rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-1"
                 style={{ background: bgWhite, boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.06)' }}>
              {[0, 150, 300].map((delay, i) => (
                <div key={i}
                     className="w-2 h-2 rounded-full animate-bounce"
                     style={{ background: isDark ? '#6C63FF' : '#a29bfe', animationDelay: `${delay}ms` }} />
              ))}
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* 长按操作菜单 */}
      {menuVisible && selectedMsg && (
        <div
          className="fixed z-50 rounded-2xl overflow-hidden shadow-2xl"
          style={{
            left: Math.min(menuPosition.x, window.innerWidth - 160),
            top: Math.min(menuPosition.y, window.innerHeight - 180),
            width: '150px',
            background: isDark ? '#2a2a4a' : '#fff',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'}`
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* 复制 */}
          <div
            className="flex items-center gap-2 px-4 py-3 cursor-pointer transition-colors text-sm"
            style={{ color: textPrimary }}
            onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : '#f5f5f5'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            onClick={() => handleCopyMsg(selectedMsg.content)}
          >
            <i className="fa-regular fa-copy w-5 text-center" style={{ color: '#6C63FF' }} />
            <span>复制</span>
          </div>

          {/* 收藏 */}
          <div
            className="flex items-center gap-2 px-4 py-3 cursor-pointer transition-colors text-sm"
            style={{ color: textPrimary }}
            onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : '#f5f5f5'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            onClick={() => handleFavoriteMsg(selectedMsg)}
          >
            <i className="fa-regular fa-star w-5 text-center" style={{ color: '#F59E0B' }} />
            <span>收藏</span>
          </div>

          {/* 删除 */}
          <div
            className="flex items-center gap-2 px-4 py-3 cursor-pointer transition-colors text-sm"
            style={{ color: '#EF4444' }}
            onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : '#f5f5f5'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            onClick={() => handleDeleteMsg(selectedMsg.id)}
          >
            <i className="fa-regular fa-trash-can w-5 text-center" />
            <span>删除</span>
          </div>
        </div>
      )}

      {/* 收藏列表提示 */}
      {favorites.length > 0 && (
        <div className="flex-shrink-0 px-3 py-1 flex items-center gap-1 overflow-x-auto"
             style={{ background: isDark ? '#252540' : '#f9fafb' }}>
          <i className="fa-regular fa-star text-xs" style={{ color: '#F59E0B' }} />
          <span className="text-[10px]" style={{ color: textMuted }}>已收藏 {favorites.length} 条</span>
        </div>
      )}

      {/* 输入区域 */}
      <div className="flex-shrink-0 px-3 py-3"
           style={{ background: bgWhite, borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}` }}>
        <div className="flex items-end gap-2.5">
          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="输入问题，回车发送..."
              rows={1}
              className="w-full px-4 py-3 resize-none outline-none transition-all"
              style={{
                background: isDark ? '#252540' : '#f4f6f9',
                color: textPrimary,
                borderRadius: '20px',
                fontSize: '14px',
                maxHeight: '120px',
                minHeight: '44px',
                border: '1.5px solid transparent',
                lineHeight: '1.5',
              }}
              onFocus={e => e.target.style.borderColor = '#6C63FF'}
              onBlur={e => e.target.style.borderColor = 'transparent'}
              onInput={(e) => {
                e.target.style.height = 'auto'
                e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'
              }}
            />
          </div>
          <button
            onClick={handleSend}
            disabled={!inputText.trim() || isTyping}
            className="w-11 h-11 rounded-full flex items-center justify-center transition-all flex-shrink-0 border-none cursor-pointer disabled:cursor-not-allowed"
            style={{
              background: inputText.trim() && !isTyping
                ? 'linear-gradient(135deg, #6C63FF, #8B5CF6)'
                : isDark ? 'rgba(255,255,255,0.06)' : '#e5e7eb',
              color: inputText.trim() && !isTyping ? '#fff' : textMuted,
              boxShadow: inputText.trim() && !isTyping ? '0 4px 12px rgba(108,99,255,0.3)' : 'none',
              opacity: inputText.trim() && !isTyping ? 1 : 0.5,
            }}
          >
            <i className="fa-solid fa-paper-plane text-sm" />
          </button>
        </div>
      </div>
    </div>
  )
}
